import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Select,
  Form,
  Input,
  Modal,
  Typography,
  Spin,
  Table,
  Card,
  Checkbox,
  Segmented,
  Space,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileDoneOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { participationOperators } from "./utils";
import TooltipIcon from "../../common/TooltipIcon";
import { showErrorNotification } from "../../../util";

const EMPTY_PARTICIPATION_CRITERIA = {
  country: { operator: "", values: [] },
  institute: { operator: "", values: [] },
  experiment: { operator: "", values: [] },
  isPrimary: false,
};

const EMPTY_INPUT_VALUES = {
  country: "",
  institute: "",
  experiment: "",
};

const CRITERIA_SECTIONS = ["country", "institute", "experiment"];

const andOrOptions = [
  { label: "AND", value: "And" },
  { label: "OR", value: "Or" },
];

const ParticipationGroupCriteria = ({
  canModifyGroup,
  participationCriteria,
  handleUpdate,
  loading,
  categoryOperator,
  onCategoryOperatorChange,
}) => {
  // --- State Variables ---
  const [criteriaList, setCriteriaList] = useState([]);
  const [currentCriteria, setCurrentCriteria] = useState(
    EMPTY_PARTICIPATION_CRITERIA
  );
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [inputValues, setInputValues] = useState({
    country: "",
    institute: "",
    experiment: "",
  });

  // --- Utility Functions ---

  /**
   * Auto-corrects operator based on value count for backend compatibility
   * @param {string} operator - The current operator
   * @param {Array} values - Array of values
   * @returns {string} The corrected operator
   */
  const correctOperatorForValues = useCallback((operator, values) => {
    const valueCount = values.length;

    if (
      (operator === "Eq" && valueCount > 1) ||
      (operator === "In" && valueCount === 1)
    ) {
      return valueCount === 1 ? "Eq" : "In";
    }

    if (
      (operator === "Neq" && valueCount > 1) ||
      (operator === "NotIn" && valueCount === 1)
    ) {
      return valueCount === 1 ? "Neq" : "NotIn";
    }

    return operator;
  }, []);

  /**
   * Processes a criteria section for backend conversion
   * @param {Object} sectionData - Section data (country, institute, experiment)
   * @param {string} backendValuesKey - The backend key for values (Country_Codes, Institutes, etc.)
   * @returns {Object|null} Processed section or null if invalid
   */
  const processSectionForBackend = useCallback(
    (sectionData, backendValuesKey) => {
      if (!sectionData?.operator || !sectionData?.values?.length) {
        return null;
      }

      const values = Array.isArray(sectionData.values)
        ? sectionData.values.filter((v) => v)
        : [sectionData.values].filter((v) => v);

      if (!values.length) return null;

      const correctedOperator = correctOperatorForValues(
        sectionData.operator,
        values
      );

      return {
        operator: correctedOperator,
        [backendValuesKey]: values,
      };
    },
    [correctOperatorForValues]
  );

  // --- Conversion Functions ---

  /**
   * Converts backend participation criteria to local format
   */
  const convertBackendToLocalCriteria = useCallback((beCriteria) => {
    if (!beCriteria) return [];

    if (beCriteria.operator === "And" && Array.isArray(beCriteria.conditions)) {
      const localCriteriaList = [];

      for (const criteria of beCriteria.conditions) {
        const local = { ...EMPTY_PARTICIPATION_CRITERIA };

        if (criteria.country) {
          local.country = {
            operator: criteria.country.operator || "",
            values: criteria.country.country_Codes || [],
          };
        }

        if (criteria.institute) {
          local.institute = {
            operator: criteria.institute.operator || "",
            values: criteria.institute.institutes || [],
          };
        }

        if (criteria.experiment) {
          local.experiment = {
            operator: criteria.experiment.operator || "",
            values: criteria.experiment.experiments || [],
          };
        }

        local.isPrimary = criteria.isPrimary || false;

        const hasAnyCriteria =
          local.country.operator ||
          local.institute.operator ||
          local.experiment.operator ||
          local.isPrimary;

        if (hasAnyCriteria) {
          localCriteriaList.push(local);
        }
      }

      return localCriteriaList;
    }

    const local = { ...EMPTY_PARTICIPATION_CRITERIA };

    if (beCriteria.country) {
      local.country = {
        operator: beCriteria.country.operator || "",
        values: beCriteria.country.country_Codes || [],
      };
    }

    if (beCriteria.institute) {
      local.institute = {
        operator: beCriteria.institute.operator || "",
        values: beCriteria.institute.institutes || [],
      };
    }

    if (beCriteria.experiment) {
      local.experiment = {
        operator: beCriteria.experiment.operator || "",
        values: beCriteria.experiment.experiments || [],
      };
    }

    local.isPrimary = beCriteria.isPrimary || false;

    const hasAnyCriteria =
      local.country.operator ||
      local.institute.operator ||
      local.experiment.operator ||
      local.isPrimary;

    return hasAnyCriteria ? [local] : [];
  }, []);

  /**
   * Converts local participation criteria to backend format
   */
  const convertLocalToBackendCriteria = useCallback(
    (localCriteriaList) => {
      if (!localCriteriaList || localCriteriaList.length === 0) return null;

      const processedCriteria = [];

      for (const criteria of localCriteriaList) {
        if (!criteria) continue;

        const result = {};

        const countrySection = processSectionForBackend(
          criteria.country,
          "country_Codes"
        );
        if (countrySection) result.country = countrySection;

        const instituteSection = processSectionForBackend(
          criteria.institute,
          "institutes"
        );
        if (instituteSection) result.institute = instituteSection;

        const experimentSection = processSectionForBackend(
          criteria.experiment,
          "experiments"
        );
        if (experimentSection) result.experiment = experimentSection;

        if (criteria.isPrimary) {
          result.isPrimary = criteria.isPrimary;
        }

        if (Object.keys(result).length > 0) {
          processedCriteria.push(result);
        }
      }

      if (processedCriteria.length === 0) return null;
      if (processedCriteria.length === 1) return processedCriteria[0];

      return {
        operator: "And",
        conditions: processedCriteria,
      };
    },
    [processSectionForBackend]
  );

  // --- Modal Management ---

  /**
   * Resets all modal-related state and closes the modal
   */
  const cleanAddEditModalClose = useCallback((setOpenModalFunc) => {
    setCurrentCriteria(EMPTY_PARTICIPATION_CRITERIA);
    setValidationErrors({});
    setTouched({});
    setInputValues(EMPTY_INPUT_VALUES);
    if (setOpenModalFunc) setOpenModalFunc(false);
    setEditIndex(null);
  }, []);

  /**
   * Updates the criteria list and calls the parent update handler
   */
  const updateCriteria = useCallback(
    (updatedCriteriaList = criteriaList) => {
      const parsedCriteria = convertLocalToBackendCriteria(updatedCriteriaList);
      handleUpdate(parsedCriteria, false, true);
    },
    [criteriaList, convertLocalToBackendCriteria, handleUpdate]
  );

  // --- Validation Functions ---

  /**
   * Validates a specific section and returns error message if invalid
   */
  const validateSection = (section, criteria) => {
    const sectionData = criteria[section];
    if (
      sectionData.operator &&
      (!sectionData.values || sectionData.values.length === 0)
    ) {
      return `Please provide ${section} values for the selected operator.`;
    }
    return "";
  };

  /**
   * Validates current criteria and returns true if valid
   */
  const isValidCriteria = (criteria) => {
    const hasCountry =
      criteria.country.operator &&
      criteria.country.values &&
      criteria.country.values.length > 0;
    const hasInstitute =
      criteria.institute.operator &&
      criteria.institute.values &&
      criteria.institute.values.length > 0;
    const hasExperiment =
      criteria.experiment.operator &&
      criteria.experiment.values &&
      criteria.experiment.values.length > 0;
    const hasPrimary = criteria.isPrimary;

    return hasCountry || hasInstitute || hasExperiment || hasPrimary;
  };

  /**
   * Validates all sections and updates validation state
   */
  const validateAllSections = useCallback(() => {
    const newTouched = { ...touched };
    const newValidationErrors = { ...validationErrors };
    let hasErrors = false;

    CRITERIA_SECTIONS.forEach((section) => {
      const touchedKey = `${section}Values`;
      const errorKey = `${section}Values`;
      const operatorTouchedKey = `${section}Operator`;
      const operatorErrorKey = `${section}Operator`;

      newTouched[touchedKey] = true;
      newTouched[operatorTouchedKey] = true;

      const error = validateSection(section, currentCriteria);
      newValidationErrors[errorKey] = error;

      const sectionData = currentCriteria[section];
      if (
        sectionData.values &&
        sectionData.values.length > 0 &&
        !sectionData.operator
      ) {
        newValidationErrors[operatorErrorKey] = "Please select an operator.";
        hasErrors = true;
      } else {
        newValidationErrors[operatorErrorKey] = "";
      }

      if (error) hasErrors = true;
    });

    setTouched(newTouched);
    setValidationErrors(newValidationErrors);

    return !hasErrors;
  }, [touched, validationErrors, currentCriteria]);

  // --- Event Handlers ---

  /**
   * Handles form field changes
   */
  const handleFieldChange = (section, field, value) => {
    if (section === "isPrimary") {
      setCurrentCriteria((prev) => ({ ...prev, isPrimary: value }));
    } else {
      setCurrentCriteria((prev) => {
        const updated = { ...prev };
        updated[section] = {
          ...prev[section],
          [field]: value,
        };
        return updated;
      });
    }
  };

  /**
   * Handles values change for array inputs - stores raw input to allow typing commas and spaces
   */
  const handleValuesChange = (section, value) => {
    setInputValues((prev) => ({ ...prev, [section]: value }));

    if (value && value.trim()) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        [`${section}Values`]: "",
      }));
    }
  };

  /**
   * Parse the raw input string into array values on blur
   */
  const handleValuesBlur = (section) => {
    const rawValue = inputValues[section];
    const values = rawValue
      ? rawValue
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v)
      : [];

    handleFieldChange(section, "values", values);
    setTouched((prev) => ({ ...prev, [`${section}Values`]: true }));
  };

  /**
   * Handles operator change for a section, clearing values when operator changes
   */
  const handleOperatorChange = (section, operator) => {
    handleFieldChange(section, "operator", operator || "");
    handleFieldChange(section, "values", []);
    setInputValues((prev) => ({ ...prev, [section]: "" }));
    setTouched((prev) => ({
      ...prev,
      [`${section}Operator`]: false,
      [`${section}Values`]: false,
    }));
  };

  /**
   * Handles opening the add modal
   */
  const handleOpenAddModal = useCallback(() => {
    setCurrentCriteria(EMPTY_PARTICIPATION_CRITERIA);
    setInputValues(EMPTY_INPUT_VALUES);
    setOpenAddModal(true);
  }, []);

  /**
   * Handles editing a criteria item
   */
  const handleEditButtonClick = useCallback(
    (index) => {
      const criteriaToEdit = { ...criteriaList[index] };
      setCurrentCriteria(criteriaToEdit);

      setInputValues({
        country: Array.isArray(criteriaToEdit.country?.values)
          ? criteriaToEdit.country.values.join(", ")
          : criteriaToEdit.country?.values || "",
        institute: Array.isArray(criteriaToEdit.institute?.values)
          ? criteriaToEdit.institute.values.join(", ")
          : criteriaToEdit.institute?.values || "",
        experiment: Array.isArray(criteriaToEdit.experiment?.values)
          ? criteriaToEdit.experiment.values.join(", ")
          : criteriaToEdit.experiment?.values || "",
      });

      setOpenEditModal(true);
      setEditIndex(index);
    },
    [criteriaList]
  );

  /**
   * Handles saving (add/edit) criteria
   */
  const handleSaveCriteria = () => {
    if (!canModifyGroup) return;
    if (!validateAllSections()) {
      return;
    }

    if (!isValidCriteria(currentCriteria)) {
      showErrorNotification(
        "Invalid Criteria",
        "Please define at least one participation criteria or check 'Is Primary'."
      );

      return;
    }

    let updatedList;
    if (editIndex !== null) {
      updatedList = [...criteriaList];
      updatedList[editIndex] = currentCriteria;
    } else {
      updatedList = [...criteriaList, currentCriteria];
    }

    setCriteriaList(updatedList);
    updateCriteria(updatedList);
    cleanAddEditModalClose(
      editIndex !== null ? setOpenEditModal : setOpenAddModal
    );
  };

  /**
   * Handles deleting selected criteria
   */
  const handleDeleteCriteria = () => {
    if (!canModifyGroup || !selectedRows.length) return;
    const updatedList = criteriaList.filter(
      (_, index) => !selectedRows.includes(index)
    );
    setCriteriaList(updatedList);
    updateCriteria(updatedList);
    setSelectedRows([]);
    setOpenDeleteModal(false);
  };

  // --- Rendering Functions ---

  /**
   * Renders a criteria section (Country, Institute, Experiment)
   */
  const renderCriteriaSection = (title, sectionKey, placeholder) => {
    const touchedKey = `${sectionKey}Values`;
    const errorKey = `${sectionKey}Values`;
    const operatorKey = `${sectionKey}Operator`;
    const currentOperator = currentCriteria[sectionKey]?.operator || "";

    return (
      <Form.Item label={title} style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Form.Item
            style={{ flex: 1, marginBottom: 0 }}
            validateStatus={
              touched[operatorKey] && validationErrors[operatorKey]
                ? "error"
                : ""
            }
            help={touched[operatorKey] && validationErrors[operatorKey]}
          >
            <Select
              placeholder="Select Operator"
              allowClear
              options={participationOperators}
              value={currentOperator === "" ? undefined : currentOperator}
              onChange={(newOperator) =>
                handleOperatorChange(sectionKey, newOperator)
              }
            />
          </Form.Item>
          <div style={{ flex: 2 }}>
            <Form.Item
              style={{ marginBottom: 0 }}
              validateStatus={
                touched[touchedKey] && validationErrors[errorKey] ? "error" : ""
              }
              help={touched[touchedKey] && validationErrors[errorKey]}
            >
              <Input
                placeholder={placeholder}
                allowClear
                value={inputValues[sectionKey] || ""}
                onChange={(e) => handleValuesChange(sectionKey, e.target.value)}
                onBlur={() => handleValuesBlur(sectionKey)}
              />
            </Form.Item>
          </div>
        </div>
      </Form.Item>
    );
  };

  /**
   * Renders the criteria value cell for the table, showing operator icons and values as needed.
   * @param {string} fieldName - The field name (country, institute, experiment)
   * @param {Object} criteriaItem - The criteria item
   * @returns {JSX.Element|string} The rendered cell content
   */
  const renderCriteriaValueCell = useCallback((fieldName, criteriaItem) => {
    const fieldData = criteriaItem[fieldName];
    if (!fieldData || !fieldData.values || fieldData.values.length === 0) {
      return "";
    }

    const { operator, values } = fieldData;
    const displayValues = Array.isArray(values) ? values.join(", ") : values;
    const isNegativeOperator = operator === "Neq" || operator === "NotIn";

    if (isNegativeOperator) {
      const tooltipTitle = operator === "Neq" ? "Not Equals" : "Not In";
      return (
        <>
          <Tooltip title={tooltipTitle} mouseEnterDelay={1}>
            <StopOutlined style={{ color: "#ff4d4f", marginRight: 4 }} />
          </Tooltip>
          {displayValues}
        </>
      );
    }

    return displayValues;
  }, []);

  // --- Table Configuration ---
  // Base columns for delete confirmation modal (without Actions)
  const baseTableColumns = useMemo(
    () => [
      {
        title: "Country",
        dataIndex: "country",
        key: "country",
        render: (_, record) => renderCriteriaValueCell("country", record),
      },
      {
        title: "Institute",
        dataIndex: "institute",
        key: "institute",
        render: (_, record) => renderCriteriaValueCell("institute", record),
      },
      {
        title: "Experiment",
        dataIndex: "experiment",
        key: "experiment",
        render: (_, record) => renderCriteriaValueCell("experiment", record),
      },
      {
        title: "Is Primary",
        dataIndex: "isPrimary",
        key: "isPrimary",
        render: (isPrimary) => (isPrimary ? "Yes" : "No"),
      },
    ],
    [renderCriteriaValueCell]
  );

  // Main columns for criteria table (with Actions)
  const columns = useMemo(
    () => [
      ...baseTableColumns,
      {
        title: "Actions",
        key: "actions",
        width: 80,
        render: (_, record, index) => (
          <Button
            icon={<EditOutlined />}
            color="primary"
            variant="filled"
            onClick={() => handleEditButtonClick(index)}
            disabled={!canModifyGroup}
          />
        ),
      },
    ],
    [baseTableColumns, canModifyGroup, handleEditButtonClick]
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedRows,
      onChange: setSelectedRows,
      getCheckboxProps: () => ({ disabled: !canModifyGroup }),
    }),
    [selectedRows, canModifyGroup]
  );

  // --- Effect Hooks ---

  useEffect(() => {
    setCriteriaList(convertBackendToLocalCriteria(participationCriteria));
  }, [participationCriteria, convertBackendToLocalCriteria]);

  // --- Render ---

  const hasCriteria = criteriaList.length > 0;
  const hasSelectedRows = selectedRows.length > 0;

  const tableDataSource = useMemo(
    () =>
      criteriaList.map((item, index) => ({
        ...item,
        key: index,
      })),
    [criteriaList]
  );

  // DataSource for delete confirmation modal (only selected rows)
  const deleteConfirmationDataSource = useMemo(
    () =>
      selectedRows.map((index) => ({
        ...criteriaList[index],
        key: index,
      })),
    [criteriaList, selectedRows]
  );

  return (
    <>
      {loading ? (
        <Card style={{ marginBottom: 16, textAlign: "center" }}>
          <Spin size="large" />
        </Card>
      ) : (
        <Card
          style={{ marginBottom: 16 }}
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                <FileDoneOutlined
                  style={{ marginRight: 8, color: "#bfbfbf" }}
                />
                Experiment Participations
              </Typography.Title>
              <Segmented
                size="small"
                value={categoryOperator}
                onChange={onCategoryOperatorChange}
                options={andOrOptions}
                disabled={!canModifyGroup || !participationCriteria}
              />
              <TooltipIcon
                size={16}
                title="Choose how criteria are combined: AND includes only people who match every condition, while OR includes people who match at least one condition. Changing this can significantly widen or narrow who is included."
              />
            </div>
          }
          extra={
            <Space>
              <Button
                color="green"
                variant="solid"
                icon={<PlusOutlined />}
                disabled={!canModifyGroup}
                onClick={handleOpenAddModal}
              >
                Add Criteria
              </Button>
            </Space>
          }
        >
          <>
            <Table
              columns={columns}
              dataSource={tableDataSource}
              pagination={false}
              rowSelection={rowSelection}
              locale={{ emptyText: "No criteria defined" }}
            />
            {hasCriteria && (
              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!hasSelectedRows || !canModifyGroup}
                  onClick={() => setOpenDeleteModal(true)}
                />
              </div>
            )}
          </>
        </Card>
      )}

      {/* Add Modal */}
      <Modal
        open={openAddModal}
        onCancel={() => cleanAddEditModalClose(setOpenAddModal)}
        width={900}
        centered
        title="Add Participation Criteria"
        footer={[
          <Button
            key="cancel"
            onClick={() => cleanAddEditModalClose(setOpenAddModal)}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            color="green"
            variant="solid"
            onClick={handleSaveCriteria}
          >
            Add
          </Button>,
        ]}
      >
        <Form layout="vertical">
          {renderCriteriaSection(
            "Country",
            "country",
            "Enter country codes (e.g., CH, FR, DE)"
          )}
          {renderCriteriaSection(
            "Institute",
            "institute",
            "Enter institute names (e.g., CERN, DESY)"
          )}
          {renderCriteriaSection(
            "Experiment",
            "experiment",
            "Enter experiment names (e.g., ATLAS, CMS)"
          )}
          <Checkbox
            checked={currentCriteria.isPrimary}
            onChange={(e) =>
              handleFieldChange("isPrimary", null, e.target.checked)
            }
          >
            <Typography.Text strong>Is Primary</Typography.Text>
          </Checkbox>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={openEditModal}
        onCancel={() => cleanAddEditModalClose(setOpenEditModal)}
        width={900}
        centered
        title="Edit Participation Criteria"
        footer={[
          <Button
            key="cancel"
            onClick={() => cleanAddEditModalClose(setOpenEditModal)}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            color="green"
            variant="solid"
            onClick={handleSaveCriteria}
          >
            Save
          </Button>,
        ]}
      >
        <Form layout="vertical">
          {renderCriteriaSection(
            "Country",
            "country",
            "Enter country codes (e.g., CH, FR, DE)"
          )}
          {renderCriteriaSection(
            "Institute",
            "institute",
            "Enter institute names (e.g., CERN, DESY)"
          )}
          {renderCriteriaSection(
            "Experiment",
            "experiment",
            "Enter experiment names (e.g., ATLAS, CMS)"
          )}
          <Checkbox
            checked={currentCriteria.isPrimary}
            onChange={(e) =>
              handleFieldChange("isPrimary", null, e.target.checked)
            }
          >
            <Typography.Text strong>Is Primary</Typography.Text>
          </Checkbox>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={openDeleteModal}
        onCancel={() => setOpenDeleteModal(false)}
        centered
        width={900}
        title={
          <span>
            <DeleteOutlined style={{ marginRight: 8, color: "#ff4d4f" }} />
            Confirm Deletion
          </span>
        }
        footer={[
          <Button key="cancel" onClick={() => setOpenDeleteModal(false)}>
            Cancel
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            onClick={handleDeleteCriteria}
          >
            Delete
          </Button>,
        ]}
      >
        <p>
          Are you sure you want to remove the selected participation criteria?
        </p>
        {selectedRows.length > 0 && (
          <Table
            columns={baseTableColumns}
            dataSource={deleteConfirmationDataSource}
            size="small"
            pagination={false}
          />
        )}
      </Modal>
    </>
  );
};

export default ParticipationGroupCriteria;
